import SyncAltIcon from "@mui/icons-material/SyncAltRounded";
import { Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { CrmObjectSyncSettings, CrmSyncObjectKey } from "../../../../api/crmConnectorsApi";
import crmConnectorsApi from "../../../../api/crmConnectorsApi";
import { useCrmConnectorContext } from "../CrmConectorContext";
import CrmObjectMapping from "./CrmObjectMapping";
import FieldMappingDialog from "./FieldMappingDialog";

interface ObjectMappingRowProps {
  objectSyncSettings: CrmObjectSyncSettings;
  onFieldMappingClick: () => void;
}

const ObjectMappingRow = ({ objectSyncSettings, onFieldMappingClick }: ObjectMappingRowProps) => (
  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
    <CrmObjectMapping
      objectSyncSettings={objectSyncSettings}
      crmFilterValue={objectSyncSettings.accountTypeFilterValue}
    />
    <Button variant="text" color="secondary" startIcon={<SyncAltIcon />} onClick={onFieldMappingClick}>
      Field Mapping
    </Button>
  </Stack>
);

const ConnectionManagement = () => {
  const { crmApiName, crmName, syncSettings } = useCrmConnectorContext();

  const [ourObjects, fetchOurObjectsError] = useFetch(crmConnectorsApi[crmApiName].getOurObjects);

  const [crmObjects, fetchCrmObjectsError] = useFetch(crmConnectorsApi[crmApiName].getCrmObjects);

  const [editObjectKey, setEditObjectKey] = useState<CrmSyncObjectKey>();

  const fetchError = fetchOurObjectsError || fetchCrmObjectsError;
  if (fetchError) {
    logError(fetchError, "[ConnectionManagement] fetch");
    return <DataLoadingFailed title="Loading connector settings failed" />;
  }

  const loading = ourObjects === undefined || crmObjects === undefined;
  if (loading) {
    return <InlineLoader />;
  }

  const handleFieldMappingClick = (key: CrmSyncObjectKey) => () => setEditObjectKey(key);

  const handleFieldMappingCancel = () => setEditObjectKey(undefined);

  const objectSyncSettings = editObjectKey && syncSettings ? syncSettings[editObjectKey] : undefined;

  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        Objects synchronization
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        Specify the data you want to synchronize
      </Typography>
      <Stack direction="row" spacing={36} sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle2">Entrilia</Typography>
        <Typography variant="subtitle2">{crmName}</Typography>
      </Stack>
      {syncSettings && (
        <ObjectMappingRow
          objectSyncSettings={syncSettings.crmContactsSyncSettings}
          onFieldMappingClick={handleFieldMappingClick("crmContactsSyncSettings")}
        />
      )}
      {syncSettings && (
        <ObjectMappingRow
          objectSyncSettings={syncSettings.crmInvestorsSyncSettings}
          onFieldMappingClick={handleFieldMappingClick("crmInvestorsSyncSettings")}
        />
      )}
      {editObjectKey && objectSyncSettings && (
        <FieldMappingDialog
          objectKey={editObjectKey}
          objectSyncSettings={objectSyncSettings}
          onCancel={handleFieldMappingCancel}
          crmObjects={crmObjects}
          ourObjects={ourObjects}
        />
      )}
    </>
  );
};

export default ConnectionManagement;
